import { useMemo, useState } from 'react';
import { ExternalLink, FileText, Leaf, Map as MapIcon, ShieldCheck } from 'lucide-react';
import { useFazendaTalhoes } from '@/modules/agronomia/api';
import { useDiagnosticoDetail } from '@/modules/diagnostico/api';
import { Button, Chip, Modal, Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/primitives';
import { formatName } from '@/lib/format';
import type { Documento } from '../schemas';

interface Props {
  cpf: string;
  imovel: Record<string, unknown>;
  docs: Documento[];
  initialDocId?: string | number | null | undefined;
  onClose: () => void;
}

const RESTRICOES_RELEVANTES = new Set([
  'cnfp',
  'prodes',
  'ibama_embargo',
  'ibama_embargo_geo',
  'embargo_estadual',
  'icmbio_autos',
  'icmbio_embargos',
  'sobreposicao',
  'sicar',
]);

const RESTRICAO_LABEL: Record<string, string> = {
  cnfp: 'Floresta Pública (CNFP)',
  prodes: 'Desmatamento (PRODES)',
  ibama_embargo: 'Embargo IBAMA — CPF',
  ibama_embargo_geo: 'Embargo IBAMA — imóvel',
  embargo_estadual: 'Embargo estadual',
  icmbio_autos: 'Autos ICMBio',
  icmbio_embargos: 'Embargos ICMBio',
  sobreposicao: 'Sobreposição territorial',
  sicar: 'Situação do CAR',
};

function asNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatHa(value: unknown) {
  const number = asNumber(value);
  return number == null
    ? 'Não informado'
    : `${number.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} ha`;
}

function statusTone(status: string): 'ok' | 'warn' | 'danger' | 'neutral' {
  if (status === 'regular') return 'ok';
  if (status === 'irregular') return 'danger';
  if (status === 'alerta' || status === 'atencao' || status === 'pendente') return 'warn';
  return 'neutral';
}

function docLabel(doc: Documento) {
  return String(doc.descricao || doc.tipo || `Documento ${doc.id ?? ''}`);
}

export function DocumentoImovelHybridModal({ cpf, imovel, docs, initialDocId, onClose }: Props) {
  const id = String(imovel.id ?? '');
  const numericId = Number(id);
  const [tab, setTab] = useState('restricoes');
  const firstDocId = initialDocId ?? docs.find((doc) => doc.id != null && doc.arquivo)?.id ?? null;
  const [selectedDocId, setSelectedDocId] = useState<string | number | null>(firstDocId);
  const { data: diagnostico, isLoading: loadingDiagnostico } = useDiagnosticoDetail(cpf);
  const { data: talhoes = [], isLoading: loadingTalhoes } = useFazendaTalhoes(
    Number.isFinite(numericId) ? numericId : null,
  );

  const diagnosticoImovel = diagnostico?.imoveis.find((item) => String(item.imovel_id) === id);
  const restricoes = (diagnosticoImovel?.verificacoes ?? []).filter((item) =>
    RESTRICOES_RELEVANTES.has(item.verificacao),
  );
  const irregulares = restricoes.filter((item) => item.status === 'irregular');
  const revisar = restricoes.filter((item) =>
    ['alerta', 'atencao', 'pendente'].includes(item.status),
  );
  const areaStatus = irregulares.length
    ? { label: 'Restrição identificada', tone: 'danger' as const }
    : revisar.length
      ? { label: 'Revisão necessária', tone: 'warn' as const }
      : restricoes.length
        ? { label: 'Sem impedimento nas verificações disponíveis', tone: 'ok' as const }
        : { label: 'Análise ainda não executada', tone: 'neutral' as const };

  const culturas = useMemo(() => {
    const result = new Map<string, { area: number; safras: Set<string> }>();
    for (const talhao of talhoes) {
      const cultura = String(talhao.cultura_atual ?? '').trim();
      if (!cultura) continue;
      const current = result.get(cultura) ?? { area: 0, safras: new Set<string>() };
      current.area += talhao.area_ha ?? 0;
      if (talhao.safra_atual) current.safras.add(talhao.safra_atual);
      result.set(cultura, current);
    }
    return [...result.entries()].sort(([a], [b]) => a.localeCompare(b, 'pt-BR'));
  }, [talhoes]);

  const nome = formatName(String(imovel.denominacao || `Imóvel #${id}`));
  const matricula = imovel.matricula
    ? `Matrícula ${String(imovel.matricula)}`
    : 'Matrícula não informada';
  const docUrl =
    selectedDocId != null
      ? `/api/v2/documentos/${encodeURIComponent(String(selectedDocId))}/arquivo`
      : null;

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose()}
      title={`Visão híbrida — ${nome}`}
      description={`${matricula} · documento, imóvel e análise no mesmo painel`}
      icon={<ShieldCheck size={20} strokeWidth={1.7} />}
      size="full"
      headerActions={
        <Button variant="ghost" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      <div className="grid min-h-[72vh] gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <section className="flex min-h-[34rem] min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-surface2">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-text">
              <FileText size={16} strokeWidth={1.6} /> Documento
            </div>
            {docUrl && (
              <Button variant="ghost" size="sm" asChild>
                <a href={docUrl} target="_blank" rel="noreferrer">
                  <ExternalLink size={13} /> Nova aba
                </a>
              </Button>
            )}
          </div>
          {docs.length > 1 && (
            <div className="border-b border-border p-2">
              <select
                value={selectedDocId == null ? '' : String(selectedDocId)}
                onChange={(event) => setSelectedDocId(event.target.value || null)}
                className="h-9 w-full rounded-md border border-border bg-elevated px-2.5 text-xs text-text"
                aria-label="Documento exibido na visão híbrida"
              >
                {docs
                  .filter((doc) => doc.id != null && doc.arquivo)
                  .map((doc) => (
                    <option key={String(doc.id)} value={String(doc.id)}>
                      {docLabel(doc)}
                    </option>
                  ))}
              </select>
            </div>
          )}
          {docUrl ? (
            <iframe
              src={docUrl}
              className="min-h-0 flex-1 border-0 bg-white"
              title="Documento do imóvel"
            />
          ) : (
            <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-text-muted">
              Nenhum arquivo disponível para visualização.
            </div>
          )}
        </section>

        <section className="flex min-h-[34rem] min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-elevated">
          <Tabs value={tab} onValueChange={setTab} className="min-h-0 flex-1">
            <TabsList className="shrink-0 overflow-x-auto px-2">
              <TabsTrigger value="restricoes">
                <ShieldCheck size={14} /> Área financiável
              </TabsTrigger>
              <TabsTrigger value="mapa">
                <MapIcon size={14} /> Imóvel no mapa
              </TabsTrigger>
              <TabsTrigger value="culturas" count={culturas.length}>
                <Leaf size={14} /> Culturas
              </TabsTrigger>
            </TabsList>

            <TabsContent value="restricoes" className="min-h-0 flex-1 overflow-y-auto p-4">
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Elegibilidade territorial
                  </p>
                  <p className="mt-1 text-lg font-semibold text-text">
                    {formatHa(imovel.area_total_ha)}
                  </p>
                  <p className="text-xs text-text-muted">
                    Área total cadastrada — não equivale automaticamente à área financiável.
                  </p>
                </div>
                <Chip tone={areaStatus.tone} size="sm" dot>
                  {areaStatus.label}
                </Chip>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <Metric label="Área produtiva informada" value={formatHa(imovel.area_lavoura_ha)} />
                <Metric
                  label="Área consolidada no CAR"
                  value={formatHa(imovel.car_area_consolidada)}
                />
                <Metric
                  label="Área financiável validada"
                  value={irregulares.length ? 'Indisponível até saneamento' : 'Pendente de cálculo'}
                />
              </div>

              <div className="mt-4 rounded-lg border border-warning/30 bg-warning/5 p-3 text-xs leading-relaxed text-text-2">
                <strong className="text-text">Área financiável validada:</strong> o número final
                depende do enquadramento da operação, finalidade, restrições territoriais e regras
                vigentes do MCR/CMN. O Hub apresenta as evidências e não presume que toda a área
                cadastrada seja financiável.
              </div>

              <h4 className="mb-2 mt-5 text-sm font-semibold text-text">
                Restrições e verificações
              </h4>
              {loadingDiagnostico ? (
                <p className="text-xs text-text-muted">Carregando diagnóstico…</p>
              ) : restricoes.length ? (
                <ul className="divide-y divide-border rounded-lg border border-border">
                  {restricoes.map((item, index) => (
                    <li
                      key={`${item.verificacao}-${index}`}
                      className="flex items-start justify-between gap-3 p-3"
                    >
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-text">
                          {RESTRICAO_LABEL[item.verificacao] ?? item.verificacao}
                        </p>
                        {item.detalhe && (
                          <p className="mt-1 text-[0.72rem] leading-relaxed text-text-muted">
                            {item.detalhe}
                          </p>
                        )}
                      </div>
                      <Chip tone={statusTone(item.status)} size="sm">
                        {item.status}
                      </Chip>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="rounded-lg border border-border p-3 text-xs text-text-muted">
                  Sem diagnóstico registrado para este imóvel. Execute a varredura antes de concluir
                  a análise de crédito.
                </p>
              )}
            </TabsContent>

            <TabsContent value="mapa" className="min-h-0 flex-1">
              <iframe
                src={`/api/v2/imoveis/${encodeURIComponent(id)}/mapa-desenho`}
                className="h-[64vh] min-h-[32rem] w-full border-0 bg-white"
                title={`Mapa do imóvel ${nome}`}
              />
            </TabsContent>

            <TabsContent value="culturas" className="min-h-0 flex-1 overflow-y-auto p-4">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h4 className="text-sm font-semibold text-text">Cultura existente no imóvel</h4>
                  <p className="text-xs text-text-muted">
                    Leitura baseada no cadastro vigente dos talhões e da safra.
                  </p>
                </div>
                <Button variant="secondary" size="sm" asChild>
                  <a
                    href={`/api/v2/imoveis/${encodeURIComponent(id)}/talhoes/mapa`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <MapIcon size={13} /> Mapa dos talhões
                  </a>
                </Button>
              </div>
              {loadingTalhoes ? (
                <p className="text-xs text-text-muted">Carregando culturas…</p>
              ) : culturas.length ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  {culturas.map(([cultura, dados]) => (
                    <div key={cultura} className="rounded-lg border border-border p-3">
                      <p className="text-sm font-semibold text-text">{formatName(cultura)}</p>
                      <p className="mt-1 text-xs text-text-muted">{formatHa(dados.area)}</p>
                      <p className="mt-1 text-[0.7rem] text-text-muted">
                        Safra: {[...dados.safras].join(', ') || 'não informada'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="rounded-lg border border-border p-3 text-xs text-text-muted">
                  Nenhuma cultura está cadastrada nos talhões deste imóvel.
                </p>
              )}
              <div className="mt-4 rounded-lg border border-border bg-surface2 p-3 text-xs leading-relaxed text-text-muted">
                Imagens de satélite e NDVI auxiliam a conferência visual, mas não são usadas
                isoladamente para declarar a espécie cultivada. A identificação exibida acima vem do
                cadastro técnico de talhão/safra.
              </div>
              <iframe
                src={`/api/v2/imoveis/${encodeURIComponent(id)}/talhoes/mapa`}
                className="mt-4 h-[36vh] min-h-[20rem] w-full rounded-lg border border-border bg-white"
                title={`Talhões e culturas de ${nome}`}
              />
            </TabsContent>
          </Tabs>
        </section>
      </div>
    </Modal>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface2 p-3">
      <p className="text-[0.68rem] font-semibold uppercase tracking-wide text-text-muted">
        {label}
      </p>
      <p className="mt-1 text-sm font-semibold text-text">{value}</p>
    </div>
  );
}
